import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import PasswordInput from "@/components/julie/PasswordInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Inscription partenaire via le lien d'invitation envoyé par Julie.
// L'espace créé est NON ACTIF : Julie analyse puis valide chaque partenaire depuis son administration.
export default function PartnerSignup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("invite") || "";
  const [invite, setInvite] = useState(null);
  const [invalid, setInvalid] = useState("");
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    name: "", last_name: "", company: "", email: "", password: "",
    phone: "", country: "", city: "", desired_type: "", main_channel: "", message: "",
  });
  const [cgu, setCgu] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!token) {
      setInvalid("Ce lien d'invitation est incomplet.");
      return;
    }
    axios
      .get(`${API}/partners/invite/${token}`)
      .then(({ data }) => {
        setInvite(data);
        if (data.email) setForm((f) => ({ ...f, email: data.email }));
      })
      .catch((err) => setInvalid(err.response?.data?.detail || "Cette invitation n'est plus valable."));
  }, [token]);

  const submit = async () => {
    if (form.name.trim().length < 2) return toast("Écrivez votre prénom.");
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return toast("Cette adresse e-mail ne semble pas valide.");
    if (form.password.length < 8) return toast("Votre mot de passe doit contenir au moins 8 caractères.");
    if (!cgu) return toast("Merci d'accepter les conditions générales d'utilisation.");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/partners/signup`, { ...form, token, cgu_accepted: cgu });
      localStorage.setItem("rav-token", data.access_token);
      setDone(true);
    } catch (err) {
      toast(err.response?.data?.detail || "Une erreur est survenue. Réessayez.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="rav-page" data-testid="partner-signup-page">
      <img src="/logo-accueil.webp" alt="Respire Aime Vibre" className="rav-page-logo" />
      {invalid ? (
        <div style={{ textAlign: "center" }}>
          <h1 className="rav-page-title">Invitation partenaire</h1>
          <p className="rav-page-sub" data-testid="partner-signup-invalid">{invalid}</p>
          <button type="button" className="rav-primary-button" data-testid="partner-signup-home" onClick={() => navigate("/")}>
            Découvrir Respire Aime Vibre
          </button>
        </div>
      ) : done ? (
        <div style={{ textAlign: "center" }}>
          <h1 className="rav-page-title">Merci {form.name} 🤎</h1>
          <p className="rav-page-sub" data-testid="partner-signup-done">
            Votre espace partenaire est créé. Julie examine et valide personnellement chaque demande :
            vous recevrez un e-mail dès l'activation de votre espace.
          </p>
          <button type="button" className="rav-primary-button" data-testid="partner-signup-space" onClick={() => navigate("/partenaire/espace")}>
            Voir mon espace partenaire
          </button>
        </div>
      ) : invite ? (
        <>
          <h1 className="rav-page-title" style={{ textAlign: "center" }}>Devenir partenaire</h1>
          <p className="rav-page-sub" style={{ textAlign: "center" }}>
            {invite.label ? `Invitation de Julie pour ${invite.label}. ` : ""}
            Créez votre espace en quelques minutes — il sera activé après validation.
          </p>
          <label className="rav-field rav-welcome-fields"><span>Votre prénom</span>
            <input className="rav-input" data-testid="psignup-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Votre prénom" />
          </label>
          <label className="rav-field rav-welcome-fields"><span>Votre nom</span>
            <input className="rav-input" data-testid="psignup-lastname" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} placeholder="Votre nom" />
          </label>
          <label className="rav-field rav-welcome-fields"><span>Société / structure (facultatif)</span>
            <input className="rav-input" data-testid="psignup-company" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Nom de votre structure" />
          </label>
          <label className="rav-field rav-welcome-fields"><span>Votre adresse e-mail</span>
            <input className="rav-input" data-testid="psignup-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value.toLowerCase())} placeholder="ex. marie@email.fr" />
          </label>
          <label className="rav-field rav-welcome-fields"><span>Votre mot de passe</span>
            <PasswordInput testId="psignup-password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="8 caractères minimum" autoComplete="new-password" />
          </label>
          <label className="rav-field rav-welcome-fields"><span>Téléphone (facultatif)</span>
            <input className="rav-input" data-testid="psignup-phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Votre numéro" />
          </label>
          <label className="rav-field rav-welcome-fields"><span>Pays / ville (facultatif)</span>
            <input className="rav-input" data-testid="psignup-country" value={form.country} onChange={(e) => set("country", e.target.value)} placeholder="ex. France, Annecy" />
          </label>
          <label className="rav-field rav-welcome-fields"><span>Votre canal principal (facultatif)</span>
            <input className="rav-input" data-testid="psignup-channel" value={form.main_channel} onChange={(e) => set("main_channel", e.target.value)} placeholder="ex. Instagram, newsletter, cabinet…" />
          </label>
          <label className="rav-field rav-welcome-fields"><span>Un mot pour Julie (facultatif)</span>
            <textarea className="rav-input" data-testid="psignup-message" rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Présentez-vous en quelques mots" />
          </label>
          <label className="rav-cgu-row" data-testid="psignup-cgu-row">
            <input type="checkbox" data-testid="psignup-cgu" checked={cgu} onChange={(e) => setCgu(e.target.checked)} />
            <span>J'accepte les conditions générales d'utilisation de Respire Aime Vibre.</span>
          </label>
          <button type="button" className="rav-primary-button" data-testid="psignup-submit" disabled={sending} onClick={submit}>
            Créer mon espace partenaire
          </button>
        </>
      ) : (
        <p className="rav-page-sub" style={{ textAlign: "center" }} data-testid="psignup-loading">Vérification de votre invitation…</p>
      )}
    </main>
  );
}
